import React from "react";
import { Form, Row, Col, message } from 'antd';
import { getLangList } from '@/lib/i18n';
import { getPagination, getPageParamObject, showDelConfirm, getOnOkFunction } from "@/lib/antdElementFunction";
import { ReButton, ReModal } from '@/components/refineElement';
import ResponsiveTable from '@/components/responsiveTable';
import LostAndFoundConfig from "@/config/lostAndFound";
import { dateFormat } from "@/lib/dateTime";
import { convertBase64ToPicture } from "@/lib/uploadFunction";
import { hasPermission } from "@/lib/permissionUtil";
import ModalInfo from "./ModalInfo";
import "./index.less";

const { getAllLostItemInfo, createLostItemInfo, updateLostItemInfo, deleteLostItemInfo } = LostAndFoundConfig;

interface state {
  isModalVisible: boolean;
  handleType: string;
  pageSize: number;
  pageNo: number;
  dataObject: any;
  itemInfo: any;
}

class LostAndFound extends React.Component<{}, state> {
  state = {
    isModalVisible: false,
    handleType: '',
    pageSize: 20,
    pageNo: 1,
    dataObject: {},
    itemInfo: {} as never as any,
  };
  buttonT: any = getLangList("button");
  lostAndFoundT: any = getLangList("lostAndFound");
  errorT: any = getLangList("error");

  componentDidMount() {
    this.getAllLostItemInfo();
  }

  getColumns = () => {
    const columns: any = [
      { title: this.lostAndFoundT.DATE, dataIndex: "foundTime" },
      { title: this.lostAndFoundT.ITEM, dataIndex: "foundItem" },
      {
        title: this.lostAndFoundT.PICTURE,
        dataIndex: "itemImg",
        render: (text: string) => <img height="100px" width="100px" alt="avatar" src={convertBase64ToPicture(text)} />
      },
      { title: this.lostAndFoundT.PLACE, dataIndex: "foundPlace" },
    ];

    hasPermission(['LOST_AND_FOUND_EDIT', 'LOST_AND_FOUND_DELETE']) && columns.push({
      title: this.lostAndFoundT.OPERATE,
      dataIndex: "action",
      render: (text: any, record: any) => {
        const { id } = record;
        return (
          <div className="action-bar">
            {hasPermission('LOST_AND_FOUND_EDIT') &&
              <div>
                <ReButton
                  iconName='edit'
                  onClick={this.clickEdit(record)}
                >
                  {this.buttonT.EDIT}
                </ReButton>
              </div>
            }
            {hasPermission('LOST_AND_FOUND_DELETE') &&
              <div>
                <ReButton
                  iconName='delete'
                  onClick={this.clickDelete(id)}
                  attributeObject={{ type: 'danger' }}
                >
                  {this.buttonT.DELETE}
                </ReButton>
              </div>
            }
          </div >
        );
      }
    });

    return columns;
  };

  getAllLostItemInfo = (pagination?: any, filters?: any, sorter?: any) => {
    const { pageSize } = this.state;
    const pageParamObject = getPageParamObject(pageSize, pagination, sorter);
    const params = {
      size: pageParamObject.nowPageSize,
      page: pageParamObject.nowPageNo - 1,
    };

    getAllLostItemInfo(params).then((res: any) => {
      const dataObject = res?.value ?? {};
      this.setState({
        pageSize: pageParamObject.nowPageSize,
        pageNo: pageParamObject.nowPageNo,
        dataObject,
      });
    });
  }

  toggleModal = (handleType: string = '') => {
    const { isModalVisible } = this.state;
    this.setState({ isModalVisible: !isModalVisible, handleType });
  }

  clickCreate = () => {
    this.setState({ itemInfo: {} }, () => this.toggleModal('create'));
  }

  clickEdit = (data: object) => () => {
    this.setState({ itemInfo: data }, () => this.toggleModal('update'));
  }

  clickDelete = (id: string) => () => {
    showDelConfirm(() => this.deleteLostItemInfo(id));
  };

  createLostItemInfo = (values: any) => {
    const { foundTime, foundItem, foundPlace, itemImg } = values;
    const itemInfo = {
      foundTime: foundTime.format(dateFormat),
      foundItem,
      foundPlace,
    };
    const params = { stringFormat: { itemInfo }, fileFormat: { itemImg } };

    createLostItemInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(this.errorT.failed);
      }
      message.success(this.errorT.success);
      this.toggleModal();
      this.getAllLostItemInfo();
    });
  }

  updateLostItemInfo = (values: any) => {
    const { itemInfo } = this.state;
    const { id } = itemInfo;
    const { foundTime, foundItem, foundPlace, itemImg } = values;
    const info = {
      foundTime: foundTime.format(dateFormat),
      foundItem,
      foundPlace,
      id,
    };
    const params = { stringFormat: { itemInfo: info }, fileFormat: { itemImg } };

    updateLostItemInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(this.errorT.failed);
      }
      message.success(this.errorT.success);
      this.toggleModal();
      this.getAllLostItemInfo();
    });
  }

  deleteLostItemInfo = (itemId: string) => {
    const params = { itemId };

    deleteLostItemInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(this.errorT.failed);
      }
      message.success(this.errorT.success);
      this.getAllLostItemInfo();
    });
  }

  render() {
    const { isModalVisible, handleType, pageSize, pageNo, dataObject, itemInfo }: any = this.state;
    const { value = [], totalElements } = dataObject;
    const okFunction = {
      create: this.createLostItemInfo,
      update: this.updateLostItemInfo,
    };

    return (
      <>
        <Row className="lost-and-found-page">
          <Col span={24}>
            <Form
              layout={"inline"}
              className="refine-form"
              onFinish={() => this.getAllLostItemInfo()}
            >
              <div className="re-button-container-right">
                <Form.Item>
                  <ReButton iconName='search' attributeObject={{ htmlType: 'submit' }}>
                    {this.buttonT.search}
                  </ReButton>
                </Form.Item>
                {hasPermission('LOST_AND_FOUND_NEW') &&
                  <Form.Item>
                    <ReButton iconName='plus' onClick={this.clickCreate}>
                      {this.buttonT.NEW}
                    </ReButton>
                  </Form.Item>
                }
              </div>
            </Form>
          </Col>

          <Col span={24}>
            <ResponsiveTable
              {...this.props}
              pagination={getPagination(pageSize, pageNo, totalElements)}
              dataSource={value}
              pageNoFun={this.getAllLostItemInfo}
              columns={this.getColumns()}
            />
          </Col>
        </Row>

        {isModalVisible &&
          <ReModal
            title={this.lostAndFoundT.ITEM}
            onCancel={() => this.toggleModal()}
            visible={isModalVisible}
            onOk={(data: any) => getOnOkFunction(handleType, data, okFunction)}
            CollectedForm={ModalInfo}
            formProps={{
              itemInfo,
              handleType,
            }}
            width={'700px'}
          />
        }
      </>
    );
  }
}

export default LostAndFound;